import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TabItem {
  id: string;
  tab_id: string;
  product_id: string | null;
  product_name: string;
  unit_price: number;
  quantity: number;
  addons: any[];
  notes: string | null;
  session_id: string | null;
  created_at: string;
}

export interface TableTab {
  id: string;
  restaurant_id: string;
  table_number: number;
  status: string;
  waiter_requested_at: string | null;
  total: number;
  created_at: string;
  closed_at: string | null;
}

function getSessionId(tableNumber: string, restaurantId: string): string {
  const key = `tab_session_${restaurantId}_${tableNumber}`;
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

export function useTableTab(restaurantId: string | null, tableNumber: string | null) {
  const [tab, setTab] = useState<TableTab | null>(null);
  const [tabItems, setTabItems] = useState<TabItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [waiterRequested, setWaiterRequested] = useState(false);
  const sessionId = useRef<string>("");

  const isTableMode = !!tableNumber && !!restaurantId;

  // Initialize: find or create open tab
  useEffect(() => {
    if (!isTableMode) { setLoading(false); return; }
    sessionId.current = getSessionId(tableNumber!, restaurantId!);

    const init = async () => {
      setLoading(true);
      try {
        // Find existing open tab for this table
        const { data: existingTab } = await supabase
          .from("table_tabs" as any)
          .select("*")
          .eq("restaurant_id", restaurantId!)
          .eq("table_number", parseInt(tableNumber!))
          .eq("status", "open")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (existingTab) {
          setTab(existingTab as any);
          setWaiterRequested(!!(existingTab as any).waiter_requested_at);
        } else {
          // Create new tab
          const { data: newTab, error } = await supabase
            .from("table_tabs" as any)
            .insert({
              restaurant_id: restaurantId!,
              table_number: parseInt(tableNumber!),
              status: "open",
            } as any)
            .select()
            .single();

          if (error) {
            console.error("Error creating tab:", error);
          } else {
            setTab(newTab as any);
          }
        }
      } catch (err) {
        console.error("Tab init error:", err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [restaurantId, tableNumber, isTableMode]);

  // Load tab items and subscribe to changes
  useEffect(() => {
    if (!tab?.id) return;

    const loadItems = async () => {
      const { data } = await supabase
        .from("table_tab_items" as any)
        .select("*")
        .eq("tab_id", tab.id)
        .order("created_at", { ascending: true });
      setTabItems((data as any[]) || []);
    };

    loadItems();

    // Subscribe to realtime changes on tab items
    const channel = supabase
      .channel(`tab-items-${tab.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "table_tab_items", filter: `tab_id=eq.${tab.id}` },
        () => { loadItems(); }
      )
      .subscribe();

    // Subscribe to tab status changes
    const tabChannel = supabase
      .channel(`tab-status-${tab.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "table_tabs", filter: `id=eq.${tab.id}` },
        (payload: any) => {
          setTab(payload.new as any);
          if (payload.new.status === "closed") {
            setTab(null);
            setTabItems([]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(tabChannel);
    };
  }, [tab?.id]);

  const addItemsToTab = useCallback(async (items: {
    productId: string;
    name: string;
    price: number;
    qty: number;
    addons: any[];
  }[]) => {
    if (!tab?.id) return false;
    try {
      const records = items.map((item) => ({
        tab_id: tab.id,
        product_id: item.productId,
        product_name: item.name,
        unit_price: item.price,
        quantity: item.qty,
        addons: item.addons,
        session_id: sessionId.current,
      }));

      const { error } = await supabase
        .from("table_tab_items" as any)
        .insert(records as any);

      if (error) {
        console.error("Error adding items:", error);
        return false;
      }

      // Update tab total
      const newTotal = tabItems.reduce((s, i) => {
        const addonTotal = (i.addons || []).reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
        return s + (Number(i.unit_price) + addonTotal) * i.quantity;
      }, 0) + items.reduce((s, i) => {
        const addonTotal = i.addons.reduce((a, ad) => a + (ad.price || 0) * (ad.qty || 1), 0);
        return s + (i.price + addonTotal) * i.qty;
      }, 0);

      await supabase
        .from("table_tabs" as any)
        .update({ total: newTotal } as any)
        .eq("id", tab.id);

      return true;
    } catch (err) {
      console.error("Add items error:", err);
      return false;
    }
  }, [tab?.id, tabItems]);

  const requestWaiter = useCallback(async () => {
    if (!tab?.id) return;
    await supabase
      .from("table_tabs" as any)
      .update({ waiter_requested_at: new Date().toISOString() } as any)
      .eq("id", tab.id);
    setWaiterRequested(true);
  }, [tab?.id]);

  const cancelWaiterRequest = useCallback(async () => {
    if (!tab?.id) return;
    await supabase
      .from("table_tabs" as any)
      .update({ waiter_requested_at: null } as any)
      .eq("id", tab.id);
    setWaiterRequested(false);
  }, [tab?.id]);

  const tabTotal = tabItems.reduce((s, i) => {
    const addonTotal = (i.addons || []).reduce((a: number, ad: any) => a + (ad.price || 0) * (ad.qty || 1), 0);
    return s + (Number(i.unit_price) + addonTotal) * i.quantity;
  }, 0);

  const tabItemCount = tabItems.reduce((s, i) => s + i.quantity, 0);

  return {
    isTableMode,
    tab,
    tabItems,
    tabTotal,
    tabItemCount,
    loading,
    waiterRequested,
    sessionId: sessionId.current,
    addItemsToTab,
    requestWaiter,
    cancelWaiterRequest,
  };
}
